import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart3, Users, Wallet, GraduationCap, TrendingUp, TrendingDown, RefreshCw, Award, AlertTriangle, Briefcase, Send, Mail, MessageSquare, Bell } from "lucide-react";
import { toast } from "sonner";
import { BIPresenceLibrary } from "./BIPresenceLibrary";

const fmt = (n) => `${Number(n||0).toLocaleString("fr-FR")} GNF`;

export default function BIDashboard() {
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bi/dashboard");
      setData(data);
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
    api.get("/weekly-report/runs").then(r => setRuns(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="p-8 text-slate-400">Chargement...</div>;

  const netResult = data.finance.net_result_ytd_gnf;
  const isProfit = netResult >= 0;

  return (
    <div className="space-y-6" data-testid="bi-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><BarChart3 className="h-3 w-3" /><span>BI — DÉCISIONNEL</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Tableau de bord décisionnel</h1>
          <p className="text-slate-500 mt-1">Consolidation temps réel de tous les indicateurs de l&apos;école.</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Actualiser</Button>
      </div>

      {/* FINANCE Section */}
      <div>
        <h2 className="font-heading font-bold text-xl text-[#1E3A8A] mb-3 flex items-center gap-2"><Wallet className="h-5 w-5" />Finance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Revenu mensuel" value={fmt(data.finance.revenue_month_gnf)} icon={TrendingUp} color="text-[#166534]" bg="bg-green-50" sub={`${data.finance.paid_transactions_month} paiements`} />
          <KPI label="Revenu YTD" value={fmt(data.finance.revenue_ytd_gnf)} icon={TrendingUp} color="text-[#1E3A8A]" bg="bg-blue-50" />
          <KPI label="Impayés totaux" value={fmt(data.finance.unpaid_total_gnf)} icon={AlertTriangle} color="text-[#EA580C]" bg="bg-orange-50" sub={`${data.finance.unpaid_count} paiements`} />
          <KPI label="Taux recouvrement" value={`${data.finance.recouvrement_rate}%`} icon={Award} color={data.finance.recouvrement_rate >= 80 ? "text-[#166534]" : "text-[#EA580C]"} bg="bg-cyan-50" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <KPI label="Masse salariale YTD" value={fmt(data.finance.payroll_ytd_gnf)} icon={TrendingDown} color="text-purple-600" bg="bg-purple-50" />
          <KPI label="Résultat net YTD" value={fmt(netResult)} icon={isProfit ? TrendingUp : TrendingDown} color={isProfit ? "text-[#166534]" : "text-red-600"} bg={isProfit ? "bg-emerald-50" : "bg-red-50"} sub={isProfit ? "Bénéfice" : "Déficit"} />
        </div>
      </div>

      {/* STUDENTS Section */}
      <div>
        <h2 className="font-heading font-bold text-xl text-[#1E3A8A] mb-3 flex items-center gap-2"><GraduationCap className="h-5 w-5" />Élèves</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPI label="Effectif total" value={data.students.total} icon={Users} color="text-[#1E3A8A]" bg="bg-blue-50" />
          <KPI label="Taux de présence" value={`${data.students.presence_rate}%`} icon={TrendingUp} color="text-[#166534]" bg="bg-green-50" />
          <KPI label="Moyenne école" value={`${data.students.school_average}/20`} icon={Award} color="text-[#EA580C]" bg="bg-orange-50" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4">
          <div className="font-heading font-bold text-lg text-[#1E3A8A] mb-3">Répartition par classe</div>
          {data.students.by_class.length === 0 ? <div className="text-sm text-slate-400">Aucune classe</div> : (
            <div className="space-y-2">
              {data.students.by_class.map(c => {
                const pct = data.students.total > 0 ? (c.count / data.students.total) * 100 : 0;
                return (
                  <div key={c.class_name}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-semibold">{c.class_name}</span>
                      <span className="tabular-nums">{c.count} élève(s)</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#1E3A8A]" style={{ width: `${pct}%` }}></div></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* STAFF Section */}
      <div>
        <h2 className="font-heading font-bold text-xl text-[#1E3A8A] mb-3 flex items-center gap-2"><Briefcase className="h-5 w-5" />Ressources Humaines</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPI label="Enseignants" value={data.staff.teachers} icon={Users} color="text-[#166534]" bg="bg-green-50" />
          <KPI label="Personnel total" value={data.staff.total_staff} icon={Users} color="text-[#1E3A8A]" bg="bg-blue-50" />
          <KPI label="Candidatures en cours" value={data.staff.candidates_pending} icon={Briefcase} color="text-[#EA580C]" bg="bg-orange-50" />
        </div>
      </div>

      {/* PRÉSENCE + PORTAIL + BIBLIOTHÈQUE (BI avancée) */}
      <BIPresenceLibrary />

      {/* COMMUNICATION FAMILLES Section */}
      {runs && (
        <div data-testid="weekly-report-history">
          <h2 className="font-heading font-bold text-xl text-[#1E3A8A] mb-3 flex items-center gap-2"><Send className="h-5 w-5" />Communication familles — Rapports hebdomadaires</h2>
          {runs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-500 text-sm">
              Aucun envoi pour l&apos;instant — le premier rapport automatique partira samedi matin (9h).
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <KPI label="Parents touchés (dernier envoi)" value={runs[0].parents_covered} icon={Users} color="text-[#1E3A8A]" bg="bg-blue-50" sub={`Semaine ${runs[0].week_key}`} />
                <KPI label="Emails envoyés" value={runs[0].emails_sent} icon={Mail} color="text-[#166534]" bg="bg-green-50" />
                <KPI label="SMS envoyés" value={runs[0].sms_sent} icon={MessageSquare} color="text-[#EA580C]" bg="bg-orange-50" />
                <KPI label="Notifications app" value={runs[0].notifications_sent} icon={Bell} color="text-purple-600" bg="bg-purple-50" />
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 mt-4 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">Historique des envois</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left">Semaine</th>
                        <th className="px-4 py-2 text-left">Envoyé le</th>
                        <th className="px-4 py-2 text-right">Parents</th>
                        <th className="px-4 py-2 text-right">Emails</th>
                        <th className="px-4 py-2 text-right">SMS</th>
                        <th className="px-4 py-2 text-right">Notifs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map(r => (
                        <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`wr-run-${r.week_key}`}>
                          <td className="px-4 py-2 font-semibold text-[#1E3A8A]">{r.week_key}</td>
                          <td className="px-4 py-2 text-slate-600">{new Date(r.ran_at).toLocaleString("fr-FR")}</td>
                          <td className="px-4 py-2 text-right tabular-nums font-bold">{r.parents_covered}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-[#166534]">{r.emails_sent}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-[#EA580C]">{r.sms_sent}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-purple-600">{r.notifications_sent}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="text-xs text-slate-400 text-right">Actualisé : {new Date(data.as_of).toLocaleString("fr-FR")}</div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, bg, sub }) {
  return (
    <div className={`${bg} rounded-2xl border border-white p-5`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</span>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div className={`text-2xl font-extrabold ${color} tabular-nums`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
